/**
 * A thread on a phone — `docs/activity.md` §7.2 (Board 16 B): its own
 * screen — ‹, the initials, the name, *<number> · Text*, a 38 px **call**
 * button and ⋯; the *Attached to* chips in one sideways row; the bubbles;
 * and the composer at the bottom.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { ConversationThread, InboxChannel } from '@rowted/shared'
import { PanelSheet } from '../../components/panel-sheet.js'
import { BackLayer, useBackLayer } from '../use-activity-history.js'
import { cn } from '../../lib/cn.js'
import { activityRequest } from '../api.js'
import { Face } from '../faces.js'
import { Glyph, type GlyphName } from '../glyphs.js'
import { useActivity } from '../activity-context.js'
import { PhoneComposer } from './composer.js'
import { FollowersSheet } from './followers.js'
import { LinksEditor } from './links-editor.js'
import { Initials, RecordChip, errorWords } from './look.js'
import { makeFromThread, type MakeKind } from './actions.js'
import { Messages, TeammateBlock, UnassignedBlock, threadSubLine, useUnreadAtOpen } from './thread.js'

const ROUND = 'inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] border border-line'

function SheetRow({ glyph, label, onClick }: { glyph: GlyphName; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="flex h-[52px] w-full items-center gap-3 border-b border-line-faint px-4 text-left text-[15px] font-medium">
      <span className="inline-flex text-text-muted"><Glyph name={glyph} size={18} /></span>{label}
    </button>
  )
}

/** **Assign to…** as a sheet — the phone's picker (§10.2: bottom sheets with 52 px rows). */
export function AssignSheet({
  thread, onClose, onDone,
}: { thread: ConversationThread; onClose: () => void; onDone: () => void }) {
  const { directory } = useActivity()
  const [error, setError] = useState<string | null>(null)
  const give = async (userId: string) => {
    try {
      await activityRequest(`/conversations/${thread.id}/assign`, { method: 'POST', body: { userId } })
      onClose()
      onDone()
    } catch (cause) {
      setError(errorWords(cause))
    }
  }
  /* Back closes it (Aaron, Oct 26). */
  useBackLayer(onClose)
  return (
    <PanelSheet open onClose={onClose} title="Assign to…" sub={thread.row.name} layer={70}>
      {thread.assignable.map((person) => {
        const face = directory?.people.find((one) => one.id === person.id)
        return (
          <button key={person.id} type="button" onClick={() => void give(person.id)}
            className="flex h-[52px] w-full items-center gap-3 border-b border-line-faint px-4 text-left text-[15px]">
            {face && <Face person={face} size={26} />}
            <span className="truncate">{person.name}</span>
          </button>
        )
      })}
      {error && <div className="px-4 py-3 text-[13px] text-danger">{error}</div>}
    </PanelSheet>
  )
}

const PHONE_MAKE: { kind: MakeKind; label: string; glyph: GlyphName }[] = [
  { kind: 'task', label: 'New task', glyph: 'check_square' },
  { kind: 'meeting', label: 'New meeting', glyph: 'calendar' },
  { kind: 'call', label: 'Log a call', glyph: 'phone' },
  { kind: 'note', label: 'New note', glyph: 'pencil' },
]

export function PhoneThread({
  thread, today, asked, onBack, onChanged, onReplaced, onRowActions, onLeft = onBack,
}: {
  thread: ConversationThread
  today: string
  asked: InboxChannel | null
  onBack: () => void
  /** The viewer took themselves off a conversation they had only by following it (Aaron, Oct 26). */
  onLeft?: () => void
  onChanged: () => void
  onReplaced: (conversationId: string) => void
  /** The list row's own menu — unread, pin, archive, categories (§7.10) — as a sheet over the thread. */
  onRowActions?: () => void
}) {
  const navigate = useNavigate()
  const unreadAtOpen = useUnreadAtOpen(thread)
  const [menu, setMenu] = useState(false)
  const [linksAt, setLinksAt] = useState<HTMLElement | null>(null)
  const [moreButton, setMoreButton] = useState<HTMLButtonElement | null>(null)
  const [assigning, setAssigning] = useState(false)
  const [following, setFollowing] = useState(false)
  const [channel, setChannel] = useState<InboxChannel | null>(asked)
  const number = thread.contact?.phone ?? thread.composer.mobiles[0]?.number ?? null
  const unassigned = thread.owner === null
  const other = thread.composer.channels.find((one) => one !== (channel ?? thread.composer.channels[0]))

  return (
    <div className="fixed inset-0 z-[60] flex h-[100dvh] flex-col bg-bg" role="dialog" aria-label={thread.row.name}>
      <div className="flex h-[58px] shrink-0 items-center gap-2.5 border-b border-line-faint bg-surface px-3">
        <button type="button" aria-label="Back to the inbox" onClick={onBack} className="inline-flex text-accent">
          <Glyph name="chevronLeft" size={20} />
        </button>
        <Initials row={thread.row} size={34} />
        <div className="flex min-w-0 flex-1 flex-col">
          <b className="truncate text-[15px]">{thread.row.name}</b>
          <span className="truncate text-[12px] text-text-muted">{threadSubLine(thread)}</span>
        </div>
        {number && (
          <a href={`tel:${number.replace(/[^\d+]/g, '')}`} aria-label={`Call ${thread.row.name}`} className={cn(ROUND, 'text-accent')}>
            <Glyph name="phone" size={18} />
          </a>
        )}
        <button ref={setMoreButton} type="button" aria-label="More" onClick={() => setMenu(true)} className={cn(ROUND, 'text-text-muted')}>
          <Glyph name="more" size={18} />
        </button>
      </div>
      {!thread.row.unknown && (
        <div className="no-scrollbar flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-line bg-surface px-3 py-2">
          <span className="whitespace-nowrap text-[11.5px] text-text-muted">Attached to</span>
          {thread.links.length === 0 && <span className="whitespace-nowrap text-[11.5px] text-text-faint">nothing yet</span>}
          {thread.links.map((link) => <RecordChip key={`${link.type}:${link.id}`} type={link.type} label={link.label} />)}
        </div>
      )}
      {(unassigned || !thread.mine) && (
        <div className="shrink-0 border-b border-line bg-surface px-3 pb-3">
          {unassigned
            ? <UnassignedBlock thread={thread} onTaken={onChanged} onReplaced={onReplaced} />
            : <TeammateBlock thread={thread} onTaken={onChanged} />}
        </div>
      )}
      <Messages thread={thread} today={today} unreadAtOpen={unreadAtOpen} phone className="bg-surface-nav px-3 pb-3 pt-1" />
      <PhoneComposer key={thread.id} thread={thread} asked={asked} channel={channel} onSent={onChanged} />

      {/* Back closes the thread's sheet before the thread (Aaron, Oct 26). */}
      {menu && <BackLayer onBack={() => setMenu(false)} />}
      <PanelSheet open={menu} onClose={() => setMenu(false)} title={thread.row.name} sub={threadSubLine(thread)} layer={70}>
        {!thread.row.unknown && (
          <SheetRow glyph="pencil" label="Change what it’s attached to" onClick={() => { setMenu(false); setLinksAt(moreButton) }} />
        )}
        {other && (
          <SheetRow glyph={other === 'email' ? 'envelope' : 'bubble'} label={other === 'email' ? 'Write an email instead' : 'Write a text instead'}
            onClick={() => { setMenu(false); setChannel(other) }} />
        )}
        {/* Who else has it in their Inbox, copied on one of its emails (Aaron, Oct 26). */}
        {thread.followers.length > 0 && (
          <SheetRow glyph="user" label={`Following · ${thread.followers.length}`}
            onClick={() => { setMenu(false); setFollowing(true) }} />
        )}
        {thread.assignable.length > 0 && (unassigned || thread.mine) && (
          <SheetRow glyph="user" label="Assign to…" onClick={() => { setMenu(false); setAssigning(true) }} />
        )}
        {thread.contact && (
          <SheetRow glyph="user" label={`Open ${thread.contact.name}`} onClick={() => navigate(`/contacts/${thread.contact!.id}`)} />
        )}
        {onRowActions && (
          <SheetRow glyph="archive" label="Mark unread, pin, archive…" onClick={() => { setMenu(false); onRowActions() }} />
        )}
        {/*
          * The desktop's **Make** (§7.1), here because the thread covers the
          * header's **+**: what is made starts attached to this thread's records
          * (§9.8, Board 16 E — *opened from Joon Kim’s thread*).
          */}
        {!thread.row.unknown && PHONE_MAKE.map((row) => (
          <SheetRow key={row.kind} glyph={row.glyph} label={row.label}
            onClick={() => { setMenu(false); makeFromThread(row.kind, thread) }} />
        ))}
      </PanelSheet>
      {linksAt && <LinksEditor thread={thread} anchor={linksAt} onClose={() => setLinksAt(null)} onSaved={onChanged} />}
      {assigning && <AssignSheet thread={thread} onClose={() => setAssigning(false)} onDone={onChanged} />}
      <FollowersSheet thread={thread} open={following} onClose={() => setFollowing(false)} onChanged={onChanged}
        onLeft={onLeft} />
    </div>
  )
}
